import React, { useEffect, useState } from 'react';
import { Lane, getLanes, saveLane, Carrier, getCarriers } from '../services/dbService';

const Lanes: React.FC = () => {
  const [lanes, setLanes] = useState<Lane[]>([]);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingLane, setEditingLane] = useState<Lane | null>(null);
  
  const [formData, setFormData] = useState<Lane>({
    origin: '',
    destination: '',
    isActive: true,
    price: 140,
    carrierId: '',
    carrierName: '',
    lumperFeeRate: 40,
    createdAt: new Date()
  });
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [fetchedLanes, fetchedCarriers] = await Promise.all([
          getLanes(),
          getCarriers()
        ]);
        setLanes(fetchedLanes);
        setCarriers(fetchedCarriers);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'carrierId' && value) {
      const selectedCarrier = carriers.find(c => c.id === value);
      setFormData(prev => ({ 
        ...prev, 
        carrierId: value,
        carrierName: selectedCarrier?.name || ''
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };
  
  const resetForm = () => {
    setFormData({
      origin: '',
      destination: '',
      isActive: true,
      price: 140,
      carrierId: '',
      carrierName: '',
      lumperFeeRate: 40,
      createdAt: new Date()
    });
    setEditingLane(null);
    setFormOpen(false);
  };
  
  const handleEdit = (lane: Lane) => {
    setEditingLane(lane);
    setFormData({ ...lane });
    setFormOpen(true);
  };
  
  const handleToggleActive = async (laneId: string, isCurrentlyActive: boolean) => {
    try {
      const laneIndex = lanes.findIndex(l => l.id === laneId);
      if (laneIndex === -1) return;
      
      const updatedLane = { ...lanes[laneIndex], isActive: !isCurrentlyActive };
      
      // Save to database
      await saveLane(updatedLane);
      
      // Update UI
      const updatedLanes = [...lanes];
      updatedLanes[laneIndex] = updatedLane;
      setLanes(updatedLanes);
    } catch (err: any) {
      console.error('Error toggling lane status:', err);
      alert(`Failed to update lane status: ${err.message}`);
    }
  };
  
  const handleDelete = async (laneId: string) => {
    if (!window.confirm('Are you sure you want to delete this lane?')) {
      return;
    }
    
    try {
      // In a real app, you would call a delete function
      // For now, we'll just update the UI
      setLanes(lanes.filter(l => l.id !== laneId));
    } catch (err: any) {
      console.error('Error deleting lane:', err);
      alert(`Failed to delete lane: ${err.message}`);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!formData.origin.trim() || !formData.destination.trim()) {
        alert('Origin and destination are required');
        return;
      }
      
      const laneToSave: Lane = {
        ...formData,
        id: editingLane?.id,
        createdAt: editingLane?.createdAt || new Date()
      };
      
      const laneId = await saveLane(laneToSave);
      
      if (editingLane) {
        // Update existing lane
        setLanes(lanes.map(l => 
          l.id === editingLane.id ? { ...laneToSave, id: laneId } : l
        ));
      } else {
        // Add new lane
        setLanes([...lanes, { ...laneToSave, id: laneId }]);
      }
      
      resetForm();
    } catch (err: any) {
      console.error('Error saving lane:', err);
      alert(`Failed to save lane: ${err.message}`);
    }
  };
  
  if (loading) {
    return <div className="loading">Loading data...</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  return (
    <div className="lanes-page">
      <div className="page-header">
        <h1>NFI Lumper Receipt Creator | Lanes</h1>
        {!formOpen && (
          <button 
            className="add-btn" 
            onClick={() => setFormOpen(true)}
          >
            Add Lane
          </button>
        )}
      </div>
      
      {formOpen && (
        <div className="form-container">
          <h2>{editingLane ? 'Edit Lane' : 'Add Lane'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="origin">Origin (City, State)*</label>
              <input 
                type="text" 
                id="origin"
                name="origin"
                value={formData.origin}
                onChange={handleInputChange}
                placeholder="e.g. Shelby, NC"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="destination">Destination (City, State)*</label>
              <input 
                type="text" 
                id="destination"
                name="destination"
                value={formData.destination}
                onChange={handleInputChange}
                placeholder="e.g. Greenwood, IN"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="carrierId">Carrier*</label>
              <select
                id="carrierId"
                name="carrierId"
                value={formData.carrierId || ''}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a carrier</option>
                {carriers.map(carrier => (
                  <option key={carrier.id} value={carrier.id}>
                    {carrier.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="price">Price ($)</label>
              <input 
                type="number" 
                id="price"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="lumperFeeRate">Lumper Fee Rate ($)</label>
              <input 
                type="number" 
                id="lumperFeeRate"
                name="lumperFeeRate"
                value={formData.lumperFeeRate || 40}
                onChange={handleInputChange}
                min="0"
                step="0.01"
              />
            </div>
            
            <div className="form-group checkbox-group">
              <label htmlFor="isActive">
                <input 
                  type="checkbox" 
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleCheckboxChange}
                />
                Active
              </label>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="save-btn">
                {editingLane ? 'Update' : 'Save'}
              </button>
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      {lanes.length === 0 ? (
        <p className="no-data">No lanes found. Add a lane to get started.</p>
      ) : (
        <div className="lanes-list">
          {lanes.map(lane => (
            <div key={lane.id} className={`lane-card ${lane.isActive ? 'active' : 'inactive'}`}>
              <div className="lane-info">
                <h3>{lane.origin} → {lane.destination}</h3>
                {lane.carrierName && (
                  <p className="lane-carrier">
                    <strong>Carrier:</strong> {lane.carrierName}
                  </p>
                )}
                <p className="lane-price">
                  <strong>Price:</strong> ${typeof lane.price === 'number' ? lane.price.toFixed(2) : Number(lane.price || 0).toFixed(2)}
                </p>
                {lane.lumperFeeRate && (
                  <p className="lane-fee">
                    <strong>Lumper Fee Rate:</strong> ${typeof lane.lumperFeeRate === 'number' ? lane.lumperFeeRate.toFixed(2) : Number(lane.lumperFeeRate || 0).toFixed(2)}
                  </p>
                )}
                <p className="status">
                  Status: <span className={lane.isActive ? 'active-status' : 'inactive-status'}>
                    {lane.isActive ? 'Active' : 'Inactive'}
                  </span>
                </p>
              </div>
              <div className="lane-actions">
                <button 
                  className="edit-btn" 
                  onClick={() => handleEdit(lane)}
                >
                  Edit
                </button>
                <button 
                  className={`toggle-btn ${lane.isActive ? 'deactivate-btn' : 'activate-btn'}`}
                  onClick={() => handleToggleActive(lane.id!, lane.isActive)}
                >
                  {lane.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button 
                  className="delete-btn" 
                  onClick={() => handleDelete(lane.id!)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Lanes; 